import * as i0 from '@angular/core';
import { Injectable, Injector, Component, Inject, Optional } from '@angular/core';
import { Subject, of, combineLatest, defer, from, timer, merge as merge$1, EMPTY } from 'rxjs';
import * as i1 from '@allianz/ngx-pfe';
import { PfeBusinessService } from '@allianz/ngx-pfe';
import * as i2 from '@allianz/taly-core';
import { AbstractBuildingBlockFacade, BUILDING_BLOCK_NAVIGATION_TYPE, isImplicitNavigation, AbstractBuildingBlockPage, BuildingBlockMetaService, EXPERIMENTAL_NEXT_BUTTON_ALWAYS_ENABLED } from '@allianz/taly-core';
import { map, startWith, tap, debounceTime, takeUntil, filter, mergeMap, distinctUntilChanged, debounce, switchMap, take, timeInterval } from 'rxjs/operators';
import { set, merge, isPlainObject, forOwn } from 'lodash';
import { TalyFrameNavigationService } from '@allianz/taly-common/frame';
import { __awaiter } from 'tslib';
import * as i3 from '@allianz/taly-common/web-components';

class PfeAclExpressionAdapter {
    constructor(pfeStateService) {
        this.pfeStateService = pfeStateService;
        this._dataChangedSubject = new Subject();
        this.queryStoreValue = (path) => {
            const value = this.pfeStateService.getStateValueByExpression(path);
            return value;
        };
        this.pfeStateService.pfeUserInputState$.subscribe(() => this.handleStateChanged());
    }
    /**
     * notify that some data in the store has changed
     * so we can invalidate the acl rules.
     */
    get dataChanged$() {
        return this._dataChangedSubject.asObservable();
    }
    handleStateChanged() {
        this._dataChangedSubject.next();
    }
}
PfeAclExpressionAdapter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: PfeAclExpressionAdapter, deps: [{ token: i1.PfeStateService }], target: i0.ɵɵFactoryTarget.Injectable });
PfeAclExpressionAdapter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: PfeAclExpressionAdapter, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: PfeAclExpressionAdapter, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.PfeStateService }]; } });

function isStoreQuery(query) {
    return query !== null && query.type === "PFE_STORE_QUERY" /* PfeStoreQuery */;
}

const VERBOSE = false;
const DEBOUNCE_STORE_UPDATES_TIME = 300;
/**
 * wrap the lodash `set` function (imported as setObject)
 * with a better name to help reading the context.
 */
function assignValueToObjectWithPath({ object, path, value }) {
    set(object, path, value);
}
/**
 * We need to deep merge resource objects
 * and a spread operator will fail this task
 * as nested keys with the same name are not merged (it's shallow)
 *
 * a = { nested : { valueA: 1}}
 * b = { nested : { valueB: 2}}
 * result = { nested : { valueA: 1, valueB: 2}}
 */
function mergeResources(objectA, objectB) {
    return merge({}, objectA, objectB);
}
function collectObjectLeaves(givenObject, predicateFn, parentKeys = []) {
    const resultEntries = [];
    const isObjectData = (probe) => isPlainObject(probe) && !isStoreQuery(probe);
    forOwn(givenObject, (objValue, key) => {
        const fullPathChain = [...parentKeys, key];
        const fullPath = transformPathChainToPath(fullPathChain);
        const objValueIsNonEmptyArray = Array.isArray(objValue) && objValue.length > 0;
        if (objValueIsNonEmptyArray || isObjectData(objValue)) {
            const result = collectObjectLeaves(objValue, predicateFn, fullPathChain);
            resultEntries.push(...result);
        }
        else if (predicateFn(objValue)) {
            resultEntries.push({ propertyPath: fullPath, value: objValue });
        }
    });
    return resultEntries;
}
/**
 *
 * @param pathPieces list of path pieces
 * @returns the joined path, generates list-like paths for numeric path pieces
 * @example ['object', 'key'] -> 'object.key'
 * ['list', '0'] -> 'list[0]'
 */
function transformPathChainToPath(pathPieces) {
    return pathPieces.reduce((acc, pathPiece) => {
        const maybeNumber = Number.parseInt(pathPiece, 10);
        if (Number.isNaN(maybeNumber)) {
            acc += acc.length > 0 ? `.${pathPiece}` : pathPiece;
        }
        else {
            acc += `[${pathPiece}]`;
        }
        return acc;
    }, '');
}
/**
 * Transform a list of resources into a stream of StaticResourceList.
 * The recipe is: extract the static data and add the latest values from all given pfe queries
 * The resulting object can then be passed to a Building Block's setResource method.
 */
function createResourceUpdateStream(resourceMap, pfeQueries) {
    const staticResourceData = getStaticResources(resourceMap);
    // extract only the pfe queries
    const queryList = getPFEQueryMap(resourceMap);
    //early exit when there are no queried data configured
    if (queryList.length === 0) {
        return of(Object.assign({}, staticResourceData));
    }
    const resourceUpdate$ = mergePfeQueryUpdates(queryList, pfeQueries.pfeExpressionObservableQuery);
    /**
     * Make sure that we always pass the static data (staticResourceData)
     * combined with the extracted pfe store values that can always be updated (resourceUpdate$)
     */
    const combinedResource$ = resourceUpdate$.pipe(map((resources) => {
        return mergeResources(staticResourceData, resources);
    }));
    return combinedResource$;
}
/**
 * Take a list of PFEResourceQueries and transform them into
 * a single map containing all mapped values from the store
 */
function mergePfeQueryUpdates(queries, pfeQueryGetter) {
    const pfeQueryToObservable = (key, query) => {
        const query$ = pfeQueryGetter(query).pipe(
        /**
         * Ensure that `combineLatest` will emit in case there is no value in the store.
         * TODO: Figure out how to handle if there is a value in the store and to prevent a null value to be emitted at all.
         */
        startWith(null), tap((value) => {
            if (VERBOSE) {
                console.log(`📚 Query Changed ${query} -> ${key}`, value);
            }
        }), map((value) => [key, value]));
        return query$;
    };
    const queryObservables = queries.map((entry) => pfeQueryToObservable(entry.key, entry.query));
    const resourceUpdate$ = combineLatest(queryObservables).pipe(map((entries) => {
        return entries.reduce((object, [path, value]) => {
            assignValueToObjectWithPath({
                object,
                path: path,
                value
            });
            return object;
        }, {});
    }), debounceTime(DEBOUNCE_STORE_UPDATES_TIME), tap((value) => {
        if (VERBOSE) {
            console.log(`📚 Resource Collection Changed`, value);
        }
    }));
    return resourceUpdate$;
}
/**
 * Given a resource list return a map of PFE Queries
 * where the key is the future key in the resource object being passed to setResources()
 * and the value is retrieved by the pfe store expression in the PFE Query.
 */
function getPFEQueryMap(resourceMap) {
    const pfeQueryLeaves = collectObjectLeaves(resourceMap, (value) => isStoreQuery(value));
    const normalized = pfeQueryLeaves.map((record) => {
        return {
            key: record.propertyPath,
            query: record.value.query
        };
    });
    return normalized;
}
/**
 * Extract all static values from a resource list
 */
function getStaticResources(resourceMap) {
    const staticLeaves = collectObjectLeaves(resourceMap, (value) => !isStoreQuery(value));
    const normalized = staticLeaves.reduce((accu, record) => {
        assignValueToObjectWithPath({
            object: accu,
            path: record.propertyPath,
            value: record.value
        });
        return accu;
    }, {});
    return normalized;
}

function tapOnce(fn) {
    return (source) => defer(() => {
        let first = true;
        return source.pipe(tap((payload) => {
            if (!first) {
                return;
            }
            fn(payload);
            first = false;
        }));
    });
}

/**
 * Create curried methods to save us from passing around the business service
 * This technique reduces our dependency to the actual PFE business service
 * and will allow use to move large parts into the ITMP core as we can't have
 * any PFE dependencies in there.
 */
const pfeObservableExpressionBuilder = (businessService) => (query) => businessService.getObservableForExpressionKey(query, true);
/**
 * The PFE Facades is our main binding between the PFE State and our Building Blocks.
 * A) State is saved into the PFE whenever it's changed
 * B) State is restored from PFE only upon connect. This means if the PFE
 * changes the store value of our Building Block while already connected our Building Block will not get that data delivered.
 * This is currently expected behavior as we really don't won't the PFE to interfere with our Building Block
 * outside of our defined lifecycle of connect/update/disconnect.
 */
class PFEFacade extends AbstractBuildingBlockFacade {
    constructor(buildingBlock, _pfeBusinessService, buildingBlockMetaService, frameNavigationService) {
        super(buildingBlock);
        this._pfeBusinessService = _pfeBusinessService;
        this.buildingBlockMetaService = buildingBlockMetaService;
        this.frameNavigationService = frameNavigationService;
        this.init();
    }
    init() {
        this.buildingBlock.businesssEventCall$
            .pipe(takeUntil(this.disconnected$), tap((businessEvent) => {
            this.handleBusinessEvent(businessEvent);
        }))
            .subscribe();
        this.stateChangesObservable = this.buildingBlock.stateChange$.pipe(takeUntil(this.disconnected$), tap(() => {
            /*
              We need to get the latest state from `getState` method instead of using the emitted
              value from the observable. This is because the `stateChanged$` is a behavior subject
              with an empty initial value. This empty value will override the existing state.
    
              Because of this call, the `getState` method of building block will be invoked twice for every state
              change (once at abstract-building-block & once here)  and the solution given via
              https://github.developer.allianz.io/it-master-platform/itmp-frontend-workspace/pull/572)
              doesn't work well. TODO: We need to find a way that the `stateChanged$` behavior subject will
              emit the initial value from state if it is available instead of empty value every time.
            */
            const state = this.buildingBlock.getState();
            this.storeBuildingBlockData(state);
        }));
        this.buildingBlock.navigateEvent$
            .pipe(takeUntil(this.disconnected$), tap((event) => {
            this.handleNavigation(event);
        }))
            .subscribe();
    }
    /**
     * This method invokes the respective service activator call
     * for the business event given. It resolves / rejects the promise
     * from the `deferred` in sync with the service activator call.
     *
     * @param {BusinessEvent} value
     */
    handleBusinessEvent({ event: businessEvent, deferred }) {
        const businessEventData = this.buildingBlockMetaService.getBusinessEvents(this.buildingBlock.id);
        if (businessEventData && Object.keys(businessEventData).includes(businessEvent)) {
            const pfeServiceId = businessEventData[businessEvent];
            console.log(`🗂 Business Event Call: ${businessEvent} -> ${pfeServiceId}`);
            // Invoking service activator call
            this._pfeBusinessService
                .triggerSingleServiceActivator(pfeServiceId)
                .then((response) => {
                deferred.resolve(response);
            })
                .catch((error) => {
                deferred.reject(error);
            });
        }
        else {
            console.warn(`Your given configuration for Building Block '${this.buildingBlock.id}' does not contain a business event called '${businessEvent}'. These are the configured business events for this Building Block:`, businessEventData);
            deferred.reject(new Error(`Business Event "${businessEvent}" does not exist for Building Block "${this.buildingBlock.id}".`));
        }
    }
    storeBuildingBlockData(data) {
        this._pfeBusinessService.storeValue(this.pfeStorageId, data);
    }
    get pfeStorageId() {
        return `${this.buildingBlock.id}`;
    }
    connect() {
        console.log(`✅ PFE Facade connecting Building Block '${this.buildingBlock.id}'`);
        if (this.buildingBlockMetaService.hasMetaData(this.buildingBlock.id)) {
            // console.log('Found Page Meta Data', this.buildingBlockMetaService.metaData);
        }
        else {
            console.log('⚠️ Could not find meta data for ', this.buildingBlock.id);
        }
        this.handleValidationConfiguration();
        // TODO: Connect can occur multiple times now
        // so initialize once and ensure that resources
        this.handleResources()
            .pipe(
        // waiting for the `setResources` method to be invoked at least once before any other lifecycle
        // methods are invoked.
        tapOnce(() => {
            this.handleState();
            super.connect();
        }))
            .subscribe();
    }
    /**
     * Implicit Page Navigation:
     * Check if we have an implicit navigation (relative or first page)
     * This will be a hard coded event in the Building Block and developers
     * can implement custom buttons that mimic the pfe actions buttons with those
     * types.
     *
     * Usage inside your Building Block:
     *  this.navigate(BUILDING_BLOCK_NAVIGATION_TYPE.Next);
     *  this.navigate(BUILDING_BLOCK_NAVIGATION_TYPE.Back);
     *  this.navigate(BUILDING_BLOCK_NAVIGATION_TYPE.Home);
     */
    processImplicitNavigation(type) {
        if (type === BUILDING_BLOCK_NAVIGATION_TYPE.Next) {
            this.frameNavigationService.navigateNext();
        }
        if (type === BUILDING_BLOCK_NAVIGATION_TYPE.Back) {
            this.frameNavigationService.navigateBack();
        }
        if (type === BUILDING_BLOCK_NAVIGATION_TYPE.Home) {
            this.frameNavigationService.navigateHome();
        }
    }
    /**
     * Explicit Page Navigation:
     * A page navigation event comes with a payload which contains a unique identifier
     * expressing the internal "keyword" the building blocks exposes in the navigation configuration.
     * The author of an application can map that internal value to an explicit value
     * we can forward to the PFE.
     *
     * INTERNAL PAGE ID -> EXTERNAL PAGE ID
     * e.g. contact -> page-b (where 'contact' is only known to the Building Block and page-b is an existing pfe id)
     *
     * The decoupling is technically required because the Building Block
     * is not allowed to encode fixed information. A page id "contact-page" is simply not existing
     * during the development of a Building Block. Someone needs to make sense of it later,
     * which is handled in the Building Block config (pages.json)
     * ```
     * {
     *     "id": "buildingBlockInstanceA",
     *     "navigation": {
     *       "contact": "page-b",
     *       "my-other-codeword": "NAVIGATE_NEXT"
     *     }
     * }
     * ```
     */
    processExplicitNavigation(type, pageMappingId) {
        if (type === BUILDING_BLOCK_NAVIGATION_TYPE.Page) {
            const navigationData = this.buildingBlockMetaService.getNavigationData(this.buildingBlock.id);
            const keys = Object.keys(navigationData);
            if (keys.includes(pageMappingId)) {
                const mappedPageIdValue = navigationData[pageMappingId];
                /**
                 * If people chose to use one of the reserved keywords
                 * for implicit navigation like NAVIGATE_NEXT or NAVIGATE_BACK
                 * for the value given inside the pages.json (instead of an explicit page id)
                 * then we want to handle it accordingly.
                 * {
                 *     "id": "buildingBlockInstanceA",
                 *     "navigation": {
                 *       "some-codeword": "NAVIGATE_NEXT",
                 *       "something-else": "my-page-id",
                 *     }
                 * }
                 */
                if (isImplicitNavigation(mappedPageIdValue)) {
                    this.processImplicitNavigation(mappedPageIdValue);
                }
                else {
                    this.frameNavigationService.gotoPage(mappedPageIdValue);
                }
            }
            else {
                console.warn(`PFE-Facade Navigation: no mapping found for id '${pageMappingId}'`);
            }
        }
    }
    handleNavigation(event) {
        if (event.type === BUILDING_BLOCK_NAVIGATION_TYPE.Page) {
            if (!event.payload) {
                throw new Error(`Building Block '${this.buildingBlock.id}' could not perform the navigation because a page was not specified.`);
            }
            this.processExplicitNavigation(event.type, event.payload);
        }
        else {
            this.processImplicitNavigation(event.type);
        }
    }
    handleValidationConfiguration() {
        if (false === this.buildingBlockMetaService.hasData(this.buildingBlock.id, 'validators')) {
            return;
        }
        const validationData = this.buildingBlockMetaService.getData(this.buildingBlock.id, 'validators');
        this.buildingBlock.setValidationConfiguration(validationData);
    }
    handleState() {
        const data = this._pfeBusinessService.getValue(this.pfeStorageId);
        this.buildingBlock.state = data;
        this.stateChangesObservable.subscribe();
    }
    handleResources() {
        if (this.buildingBlockMetaService.hasResources(this.buildingBlock.id)) {
            // that's what we authored in pages.json (static + PFE queries)
            const resourceMap = this.buildingBlockMetaService.getResources(this.buildingBlock.id);
            // prepare a curried version of the PFE store methods
            // to keep the dependency from the actual createResourceUpdateStream
            const pfeExpressionObservableQuery = pfeObservableExpressionBuilder(this._pfeBusinessService);
            const pfeResourceUpdate$ = createResourceUpdateStream(resourceMap, {
                pfeExpressionObservableQuery
            });
            // kick off the resource update stream
            return pfeResourceUpdate$.pipe(takeUntil(this.disconnected$), tap((resources) => {
                this.buildingBlock.resources = resources;
            }));
        }
        // When the resources are not configured, should return an empty value
        return of({});
    }
    disconnect() {
        console.log(`👣 PFE Facade disconnecting from Building Block '${this.buildingBlock.id}'`);
        super.disconnect();
    }
}

// eslint-disable-next-line @angular-eslint/component-class-suffix
class PFEBuildingBlockPage extends AbstractBuildingBlockPage {
    constructor(injector) {
        super(injector);
        this.pfeBusinessService = injector.get(PfeBusinessService);
        this.buildingBlockMetaService = injector.get(BuildingBlockMetaService);
        this.frameNavigationService = injector.get(TalyFrameNavigationService);
    }
    onPageComplete() {
        this.pfeBusinessService.setPageStatus(true);
    }
    onPageWaiting() {
        this.pfeBusinessService.setPageStatus(false);
    }
    createFacade(block) {
        return new PFEFacade(block, this.pfeBusinessService, this.buildingBlockMetaService, this.frameNavigationService);
    }
}
PFEBuildingBlockPage.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: PFEBuildingBlockPage, deps: [{ token: Injector }], target: i0.ɵɵFactoryTarget.Component });
PFEBuildingBlockPage.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.9", type: PFEBuildingBlockPage, selector: "ng-component", usesInheritance: true, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: PFEBuildingBlockPage, decorators: [{
            type: Component,
            args: [{
                    template: ''
                }]
        }], ctorParameters: function () {
        return [{ type: i0.Injector, decorators: [{
                        type: Inject,
                        args: [Injector]
                    }] }];
    } });

class EventsToPFEActionsAdapter {
    constructor(pfeActionsService) {
        this.pfeActionsService = pfeActionsService;
    }
    registerEvent(actionType, actionFn) {
        this.pfeActionsService.registerAction(actionType, actionFn);
    }
}
EventsToPFEActionsAdapter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: EventsToPFEActionsAdapter, deps: [{ token: i1.PfeActionsService }], target: i0.ɵɵFactoryTarget.Injectable });
EventsToPFEActionsAdapter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: EventsToPFEActionsAdapter, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: EventsToPFEActionsAdapter, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.PfeActionsService }]; } });

function createPageActionStatusObservable(pfeServiceActivatorService, pfeNavigationService, pfeConfigurationService, pfeBusinessService, experimentalNextButtonAlwaysEnabled, backLinkUtilsService) {
    const navOrServiceActivatorInProgress$ = createNavOrServiceActivatorInProgressObservable(pfeServiceActivatorService, pfeNavigationService);
    const currentPageConfig$ = pfeNavigationService.currentPageId$.pipe(filter((pageId) => Boolean(pageId)), mergeMap((pageId) => {
        return from(pfeConfigurationService.getPageConfiguration(pageId));
    }));
    // Back button should be disabled if Navigation|Service_Activator is in progress
    const disableBack$ = navOrServiceActivatorInProgress$;
    // TODO: remove this conditional logic once we make this experiment the default behavior
    let disableNext$;
    if (experimentalNextButtonAlwaysEnabled === true) {
        // Next button should be disabled if Navigation|Service_Activator is in progress
        disableNext$ = navOrServiceActivatorInProgress$;
    }
    else {
        // Next button should be disabled if the current page is invalid or Navigation|Service_Activator is in progress.
        disableNext$ = combineLatest([
            pfeNavigationService.pageStatus$,
            navOrServiceActivatorInProgress$
        ]).pipe(map(([isPageValid, inProgress]) => {
            return !isPageValid || inProgress;
        }));
    }
    const lastPageObservable$ = createLastPageObservable(pfeBusinessService, pfeNavigationService, pfeConfigurationService);
    return combineLatest([disableBack$, disableNext$, currentPageConfig$, lastPageObservable$]).pipe(map(([disableBack, disableNext, pageConfig, isLastPage]) => {
        var _a, _b;
        const backLinkFeatureActiveForActions = backLinkUtilsService.isBackLinkFeatureActiveForActions(isLastPage);
        return {
            backDisabled: disableBack,
            nextDisabled: disableNext,
            nextHidden: backLinkFeatureActiveForActions ? false : ((_a = pageConfig === null || pageConfig === void 0 ? void 0 : pageConfig.hideNextButton) !== null && _a !== void 0 ? _a : false),
            backHidden: (_b = pageConfig === null || pageConfig === void 0 ? void 0 : pageConfig.hideBackButton) !== null && _b !== void 0 ? _b : false,
            isLastPage
        };
    }));
}
function createLastPageObservable(pfeBusinessService, pfeNavigationService, pfeConfigurationService) {
    return pfeNavigationService.currentPageId$.pipe(filter((pageId) => Boolean(pageId)), mergeMap((pageId) => {
        return from(pfeConfigurationService.getPageNavigationConfiguration(pageId));
    }), mergeMap((currentPageNavConfig) => {
        if (!currentPageNavConfig)
            return of(false);
        return from(pfeBusinessService.getNextPage(currentPageNavConfig));
    }), map((pageId) => !pageId));
}
function createNavOrServiceActivatorInProgressObservable(pfeServiceActivatorService, pfeNavigationService) {
    return combineLatest([
        pfeServiceActivatorService.serviceActivatorCallInProgress$,
        pfeNavigationService.navigationInProgress$
    ]).pipe(map(([serviceActivator, navigation]) => {
        return serviceActivator || navigation;
    }));
}

const PFE_HISTORY_KEY = 'pfeHistory';
class PfeFrameNavigationService extends TalyFrameNavigationService {
    constructor(pfeBusinessService, pfeConfigurationService, pfeStateService, pfeServiceActivatorService, pfeNavigationService, talyPageService, backLinkUtilsService, experimentalNextButtonAlwaysEnabled) {
        super();
        this.pfeBusinessService = pfeBusinessService;
        this.pfeConfigurationService = pfeConfigurationService;
        this.pfeStateService = pfeStateService;
        this.pfeServiceActivatorService = pfeServiceActivatorService;
        this.pfeNavigationService = pfeNavigationService;
        this.talyPageService = talyPageService;
        this.backLinkUtilsService = backLinkUtilsService;
        this.experimentalNextButtonAlwaysEnabled = experimentalNextButtonAlwaysEnabled;
        this.APPLICATION_BUSY_DELAY = 1000;
        this.MINIMUM_MESSAGE_TIME = 1000;
        this.init();
    }
    init() {
        createPfeHistoryObservable(this.pfeStateService)
            .pipe(tap((value) => {
            this.updateHistory(value.history);
        }))
            .subscribe();
        this.pageActionStatus$ = createPageActionStatusObservable(this.pfeServiceActivatorService, this.pfeNavigationService, this.pfeConfigurationService, this.pfeBusinessService, this.experimentalNextButtonAlwaysEnabled, this.backLinkUtilsService);
        this.initApplicationBusy$();
        this.pfeBusinessService.errorMessage$
            .pipe(tap((message) => {
            this._networkErrorMessage$.next(message);
        }))
            .subscribe();
    }
    navigateNext() {
        if (this.experimentalNextButtonAlwaysEnabled) {
            this.talyPageService.onNextPageRequested();
        }
        this.pfeBusinessService.navigateNext();
    }
    navigateBack() {
        this.pfeBusinessService.navigateBack();
    }
    navigateHome() {
        return __awaiter(this, void 0, void 0, function* () {
            const firstPageId = yield this.pfeBusinessService.getFirstPage();
            if (!firstPageId) {
                throw new Error('Trying to navigate to the Homepage: PFE returned an undefined first page.');
            }
            this.gotoPage(firstPageId);
        });
    }
    gotoPage(pageId) {
        this.pfeBusinessService.navigateToPage(pageId);
    }
    /**
     * Method to navigate to a particular section with given section id
     *
     * @todo Implement this function. It might not be always the first
     * page of the section. It might change based on the conditions in
     * pfe navigation configuration as well.
     */
    gotoSection(sectionId) {
        const sections = this._visibleSections$.value;
        const section = sections.get(sectionId);
        const pages = (section === null || section === void 0 ? void 0 : section.pages) || [];
        if ((pages === null || pages === void 0 ? void 0 : pages.length) === 0) {
            throw new Error(`Could not find proper configuration to navigate to the section ${sectionId}`);
        }
        // Navigating to first page of the section
        this.gotoPage(pages[0]);
    }
    /**
     * Method to navigate to the error page
     *
     */
    gotoErrorPage(errorResponse) {
        this.pfeBusinessService.navigateToErrorPage(errorResponse);
    }
    initApplicationBusy$() {
        const busy$ = combineLatest([
            this.pfeServiceActivatorService.serviceActivatorCallInProgress$,
            this.pfeNavigationService.navigationInProgress$
        ]).pipe(map(([serviceActivatorCallInProgress, navigationInProgress]) => serviceActivatorCallInProgress || navigationInProgress), distinctUntilChanged());
        this.applicationBusy$ = busy$.pipe(debounce((busy) => (busy ? timer(this.APPLICATION_BUSY_DELAY) : timer(0))));
        this.initApplicationBusyMessage$(busy$);
    }
    initApplicationBusyMessage$(busy$) {
        const message$ = this.pfeServiceActivatorService.serviceActivatorCallInProgressDetails$.pipe(filter((configs) => Boolean(configs === null || configs === void 0 ? void 0 : configs.length)), map((configs) => { var _a; 
        // If multiple service activators are running at the same time
        // Use the first one that has displayMessage config
        return (_a = configs.find((config) => config.displayMessage)) === null || _a === void 0 ? void 0 : _a.displayMessage; }));
        // Emit a new message every 1 second
        const minimumDisplay$ = timer(0, this.MINIMUM_MESSAGE_TIME).pipe(switchMap(() => message$.pipe(take(1))));
        // Switch a message if it's been displayed longer than 1 second
        const messageSwitcher$ = message$.pipe(timeInterval(), filter(({ interval }) => interval > this.MINIMUM_MESSAGE_TIME), map(({ value }) => value));
        const applicationBusyMessage$ = merge$1(minimumDisplay$, messageSwitcher$).pipe(distinctUntilChanged());
        this.applicationBusyMessage$ = busy$.pipe(switchMap((isBusy) => {
            if (isBusy) {
                return applicationBusyMessage$;
            }
            return EMPTY;
        }));
    }
    getVisibleSections$(sections) {
        const conditionalSections = Array.from(sections).filter(([, section]) => section.visibleIf !== undefined);
        if (conditionalSections.length === 0) {
            return of(sections);
        }
        const conditions$ = conditionalSections.map(([sectionId, section]) => {
            return (this.pfeBusinessService
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                .getObservableForExpressionKey(section.visibleIf)
                .pipe(startWith(false), map((stateValue) => [sectionId, Boolean(stateValue)])));
        });
        return combineLatest(conditions$).pipe(map((conditions) => {
            const conditionMap = new Map(conditions);
            return filterSections(sections, conditionMap);
        }));
    }
}
PfeFrameNavigationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: PfeFrameNavigationService, deps: [{ token: i1.PfeBusinessService }, { token: i1.PfeConfigurationService }, { token: i1.PfeStateService }, { token: i1.PfeServiceActivatorService }, { token: i1.PfeNavigationService }, { token: i2.TalyPageService }, { token: i3.BackLinkUtilsService }, { token: EXPERIMENTAL_NEXT_BUTTON_ALWAYS_ENABLED, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
PfeFrameNavigationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: PfeFrameNavigationService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: PfeFrameNavigationService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: i1.PfeBusinessService }, { type: i1.PfeConfigurationService }, { type: i1.PfeStateService }, { type: i1.PfeServiceActivatorService }, { type: i1.PfeNavigationService }, { type: i2.TalyPageService }, { type: i3.BackLinkUtilsService }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [EXPERIMENTAL_NEXT_BUTTON_ALWAYS_ENABLED]
                    }] }];
    } });
function createPfeHistoryObservable(pfeStateService) {
    return pfeStateService.getObservableForKey(PFE_HISTORY_KEY).pipe(map((history) => {
        let currentPage = null;
        if (history.length > 0) {
            currentPage = history[history.length - 1];
        }
        return { history, currentPage };
    }));
}
function filterSections(allSections, conditions) {
    return new Map(Array.from(allSections).filter(([sectionId]) => {
        const hasCondition = conditions.has(sectionId);
        const conditionIsTrue = conditions.get(sectionId) === true;
        return !hasCondition || conditionIsTrue;
    }));
}

class PfeApiCommunicationService {
    constructor(injector) {
        this.pfeBusinessService = injector.get(PfeBusinessService);
    }
    triggerApiRequest({ name }) {
        return this.pfeBusinessService.triggerSingleServiceActivator(name);
    }
}
PfeApiCommunicationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: PfeApiCommunicationService, deps: [{ token: Injector }], target: i0.ɵɵFactoryTarget.Injectable });
PfeApiCommunicationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: PfeApiCommunicationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: PfeApiCommunicationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () {
        return [{ type: i0.Injector, decorators: [{
                        type: Inject,
                        args: [Injector]
                    }] }];
    } });

/**
 * Generated bundle index. Do not edit.
 */

export { EventsToPFEActionsAdapter, PFEBuildingBlockPage, PFEFacade, PFE_HISTORY_KEY, PfeAclExpressionAdapter, PfeApiCommunicationService, PfeFrameNavigationService, isStoreQuery, pfeObservableExpressionBuilder, tapOnce };
